import { Component, type ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';

import { Button, Callout } from '@/components/ui';

/**
 * What a screen shows when its data does not arrive. R-143.
 *
 * There was no such component. A route loader that rejected left the router
 * showing **the previous screen**, with the address bar already changed to the
 * new one. Signing in to the admin area looked like this: the URL became
 * `/admin`, the sign-in form stayed on the page, and its button sat on
 * "Signing in" for ever. No error appeared anywhere, including the console.
 *
 * A visible failure is worth more than a tidy one. This says what broke, and
 * gives the two ways out that ever help: try again, or go back to the shop.
 */
export function RouteError({ error }: { error: unknown }) {
  const router = useRouter();
  const message = error instanceof Error ? error.message : String(error ?? 'Unknown error');

  return (
    <div className="mx-auto max-w-[720px] px-5 py-16">
      <Callout tone="danger" title="This screen could not load its data">
        <p className="mt-1">{message}</p>
      </Callout>
      <div className="mt-6 flex flex-wrap gap-3">
        <Button onClick={() => void router.invalidate()}>Try again</Button>
        <Button weight="secondary" onClick={() => { window.location.href = '/'; }}>
          Back to the shop
        </Button>
      </div>
    </div>
  );
}

/**
 * The last resort, above the router.
 *
 * `defaultErrorComponent` covers a loader that rejects **after** the router has
 * mounted. It does not cover one that rejects during the first load, and that
 * is the case that happened: React rendered nothing at all, `#root` had zero
 * children, and the browser showed the page background and no text. Not an
 * error, not a spinner, not an empty state. Nothing.
 *
 * A class component, because that is still the only way to catch a render
 * error in React.
 */
export class AppErrorBoundary extends Component<
  { children: ReactNode },
  { error: unknown }
> {
  state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: unknown) {
    // Say it out loud as well. A blank page with a silent console is the worst
    // thing this application can do to somebody (R-143).
    console.error('[shopace] the application could not render:', error);
  }

  render() {
    if (!this.state.error) return this.props.children;

    const message =
      this.state.error instanceof Error
        ? this.state.error.message
        : String(this.state.error);

    return (
      <div className="mx-auto max-w-[720px] px-5 py-16">
        <Callout tone="danger" title="This page could not load">
          <p className="mt-1">{message}</p>
        </Callout>
        <div className="mt-6">
          <Button onClick={() => window.location.reload()}>Try again</Button>
        </div>
      </div>
    );
  }
}

/**
 * What a screen shows WHILE its data is on the way.
 *
 * There was nothing. The router waits for the loader, renders no component
 * while it waits, and the customer looks at an empty page. On a fast database
 * that is 80 milliseconds and nobody sees it. Pointed at a database that does
 * not answer, it was fifteen seconds of blank cream, and I mistook it for the
 * blank page of R-143 coming back.
 *
 * A visible wait is worth more than a tidy one, which is the same rule the
 * error component above was written for. `defaultPendingMs` keeps it off the
 * screen for the common fast case, so this appears only when there is really
 * something to wait for. R-184.
 */
export function RoutePending() {
  return (
    <div className="mx-auto max-w-[720px] px-5 py-24 text-center">
      <div
        className="mx-auto h-7 w-7 animate-spin rounded-full border-2 border-line border-t-brand"
        role="status"
        aria-label="Loading"
      />
      <p className="mt-4 text-[15px] text-muted">One moment.</p>
    </div>
  );
}
